import { useEffect, useState } from "react";

interface OnyxCountdownProps {
  /** ISO date-time string of the wedding. */
  dateTime: string;
  /** Crimson accent for the unit labels. */
  accent: string;
}

function useCountdown(targetMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, targetMs - now);
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    mins: Math.floor((diff % 3_600_000) / 60_000),
    secs: Math.floor((diff % 60_000) / 1_000),
    isPast: diff === 0,
  };
}

/**
 * ONYX live countdown — isolated so its per-second state update re-renders only
 * this small component, never the whole OnyxLayout (react-best-practices).
 *
 * Purely presentational + timer: the entrance reveal is owned by the parent's
 * GSAP context, which scrub-reveals `.onx-cd-unit` (scroll-bound, consistent
 * with every other Onyx reveal).
 */
export function OnyxCountdown({ dateTime, accent }: OnyxCountdownProps) {
  const { days, hours, mins, secs, isPast } = useCountdown(new Date(dateTime).getTime());

  if (isPast) return null;

  const units: { value: number; label: string }[] = [
    { value: days, label: "GÜN" },
    { value: hours, label: "SAAT" },
    { value: mins, label: "DAKİKA" },
    { value: secs, label: "SANİYE" },
  ];

  return (
    <div className="onx-countdown flex items-start justify-center gap-5 sm:gap-10">
      {units.map(({ value, label }) => (
        <div key={label} className="onx-cd-unit flex flex-col items-center">
          <span
            className="font-light tabular-nums leading-none tracking-[-0.02em]"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.25rem, 11vw, 4.5rem)",
            }}
          >
            {String(value).padStart(2, "0")}
          </span>
          <span
            className="mt-3 text-[9px] sm:text-[10px] uppercase tracking-[0.3em]"
            style={{ color: accent }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}
